import { EVENT } from '@/analytics';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import { useGetPageQuery, useUpdatePageInfoMutation, useUpdatePageMetadataMutation } from '@/store';
import { IPageMetadata, errToString } from '@typedream/data';
import mixpanel from 'mixpanel-browser';
import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PageMetadataTab } from '../../Menu/Pages/Option/types';
import {
  IOptionFacebook,
  IOptionMeta,
  IOptionSettings,
  IOptionTwitter,
  PageOptionContextProviderProps,
  PageOptionContextValue,
  PageOptionDispatchContextValue,
} from './type';

const INITIAL_CONTEXT_VALUE: PageOptionContextValue = {
  space: undefined,
  page: undefined,
  path: [],
  settings: {
    name: '',
    slug: '',
    public: false,
    duplicate: false,
  },
  meta: {
    title: '',
    description: '',
  },
  twitter: {
    title: '',
    description: '',
    username: '',
    imageURL: '',
  },
  twitterImage: null,
  facebook: {
    title: '',
    description: '',
    imageURL: '',
  },
  facebookImage: null,
  isSubmitting: false,
};

const INITIAL_DISPATCH_CONTEXT_VALUE = {
  setSettings: () => null,
  setMeta: () => null,
  setTwitter: () => null,
  setTwitterImage: () => null,
  setFacebook: () => null,
  setFacebookImage: () => null,
  onCancel: () => null,
  onSave: () => null,
};

export const PageOptionContext = createContext<PageOptionContextValue>(INITIAL_CONTEXT_VALUE);
export const PageOptionDispatchContext = createContext<PageOptionDispatchContextValue>(
  INITIAL_DISPATCH_CONTEXT_VALUE
);

export function PageOptionContextProvider({
  space,
  pageID,
  path,
  children,
}: PropsWithChildren<PageOptionContextProviderProps>) {
  const [spaceID] = useSpaceAndPageIDFromRouter();
  const [settings, setSettings] = useState<IOptionSettings>(INITIAL_CONTEXT_VALUE.settings);
  const [meta, setMeta] = useState<IOptionMeta>(INITIAL_CONTEXT_VALUE.meta);
  const [twitter, setTwitter] = useState<IOptionTwitter>(INITIAL_CONTEXT_VALUE.twitter);
  const [twitterImage, setTwitterImage] = useState<File>(null);
  const [facebook, setFacebook] = useState<IOptionFacebook>(INITIAL_CONTEXT_VALUE.facebook);
  const [facebookImage, setFacebookImage] = useState<File>(null);

  const { data: page } = useGetPageQuery([spaceID, pageID]);

  const onCancel = useCallback(() => {
    if (!!space && !!page) {
      setSettings({
        name: page.name,
        slug: path?.[path.length - 1] || '',
        public: page.public,
        duplicate: page.allow_duplicate,
      });
      setMeta({
        title: page.metadata?.metatags?.title || '',
        description: page.metadata?.metatags?.description || '',
      });
      setTwitter({
        title: page.metadata?.twitter?.title || '',
        description: page.metadata?.twitter?.description || '',
        username: page.metadata?.twitter?.username || space.metadata?.twitter?.username || '',
        imageURL: page.metadata?.twitter?.image || '',
      });
      setTwitterImage(null);
      setFacebook({
        title: page.metadata?.facebook?.title || '',
        description: page.metadata?.facebook?.description || '',
        imageURL: page.metadata?.facebook?.image || '',
      });
      setFacebookImage(null);
    }
  }, [space, page, path]);

  useEffect(() => {
    onCancel();
  }, [onCancel]);

  const { mutate: updatePageInfo, isLoading: updatePageInfoLoading } = useUpdatePageInfoMutation();
  const { mutate: updatePageMetadata, isLoading: updatePageMetadataLoading } =
    useUpdatePageMetadataMutation();

  const onSave = useCallback(
    async (tab: PageMetadataTab, cb: () => Promise<void> = () => null) => {
      try {
        if (tab === PageMetadataTab.DEFAULT) {
          await updatePageInfo({
            spaceID,
            pageID,
            name: settings.name,
            slug: settings.slug,
            isPublic: settings.public,
            allowDuplicate: settings.duplicate,
          }).unwrap();
        }

        const updatedMetada: Partial<IPageMetadata> = { ...(page?.metadata || {}) };

        if (tab === PageMetadataTab.METADATA) {
          updatedMetada.metatags = meta;
        }
        if (tab === PageMetadataTab.TWITTER) {
          updatedMetada.twitter = {
            title: twitter.title,
            description: twitter.description,
            image: twitter.imageURL,
            username: twitter.username,
          };
        }
        if (tab === PageMetadataTab.FACEBOOK) {
          updatedMetada.facebook = {
            title: facebook.title,
            description: facebook.description,
            image: facebook.imageURL,
          };
        }
        await updatePageMetadata({
          spaceID,
          pageID,
          metadata: updatedMetada,
          newTwitterImage: twitterImage,
          newFacebookImage: facebookImage,
        });
        mixpanel.track(EVENT.ADDED_SEO, {
          seo_entry_point: 'page_settings',
        });

        await cb();
      } catch (error) {
        toast.error(errToString(error));
      }
    },
    [spaceID, page, settings, meta, twitter, facebook, twitterImage, facebookImage]
  );

  const value = useMemo<PageOptionContextValue>(
    () => ({
      space,
      page,
      path,
      settings,
      meta,
      twitter,
      twitterImage,
      facebook,
      facebookImage,
      isSubmitting: updatePageMetadataLoading || updatePageInfoLoading,
    }),
    [
      space,
      page,
      path,
      settings,
      meta,
      twitter,
      twitterImage,
      facebook,
      facebookImage,
      updatePageMetadataLoading,
      updatePageInfoLoading,
    ]
  );

  const dispatchValue = useMemo<PageOptionDispatchContextValue>(
    () => ({
      setSettings,
      setMeta,
      setTwitter,
      setTwitterImage,
      setFacebook,
      setFacebookImage,
      onCancel,
      onSave,
    }),
    [onCancel, onSave]
  );

  return (
    <PageOptionContext.Provider value={value}>
      <PageOptionDispatchContext.Provider value={dispatchValue}>
        {children}
      </PageOptionDispatchContext.Provider>
    </PageOptionContext.Provider>
  );
}
